import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import FollowButton from './FollowButton';
import ImageCropModal from './ImageCropModal';
import useAuth from '../../hooks/useAuth';

/**
 * ProfileHeader component for user profile
 * @param {Object} props - Component props
 * @param {Object} props.profileUser - Profile user data
 * @param {boolean} props.isFollowing - Whether the current user is following this user
 * @param {Function} props.onFollowChange - Callback for follow status change
 * @param {boolean} props.isProfileOwner - Whether the current user owns this profile
 * @param {Function} props.onUpdateProfile - Callback for updating profile
 * @param {Function} props.onUploadProfileImage - Callback for uploading profile image
 * @param {Function} props.onUploadCoverImage - Callback for uploading cover image
 * @param {Object} props.loading - Loading states for various operations
 * @param {Object} props.error - Error states for various operations
 * @param {Object} props.uploadProgress - Progress for uploads
 * @returns {JSX.Element} ProfileHeader component
 */
const ProfileHeader = ({ 
  profileUser, 
  isFollowing, 
  onFollowChange, 
  isProfileOwner,
  onUpdateProfile,
  onUploadProfileImage,
  onUploadCoverImage,
  loading = {},
  error = {},
  uploadProgress = {}
}) => {
  const { isAuth } = useAuth();
  const profileImageInputRef = useRef();
  const coverImageInputRef = useRef();
  const [imageError, setImageError] = useState('');
  
  // Image cropping states
  const [selectedImage, setSelectedImage] = useState(null);
  const [showProfileCropModal, setShowProfileCropModal] = useState(false);
  const [showCoverCropModal, setShowCoverCropModal] = useState(false);
  
  // Edit mode states
  const [editMode, setEditMode] = useState({
    name: false,
    username: false,
    bio: false,
    skills: false
  });

  // Edit data
  const [editData, setEditData] = useState({
    name: profileUser.name || '',
    username: profileUser.username || '',
    bio: profileUser.bio || '',
    skills: profileUser.skills || [],
    newSkill: ''
  });
  
  // Format profile data
  const joinedDate = new Date(profileUser.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Toggle edit mode for specific field
  const toggleEditMode = (field, value) => {
    setEditMode(prev => ({ ...prev, [field]: value }));
    if (value) {
      // Reset to original value when entering edit mode
      if (field === 'skills') {
        setEditData(prev => ({ 
          ...prev, 
          [field]: [...(profileUser[field] || [])],
          newSkill: ''
        }));
      } else {
        setEditData(prev => ({ ...prev, [field]: profileUser[field] || '' }));
      }
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  // Handle skills management
  const handleSkillInputChange = (e) => {
    setEditData(prev => ({ ...prev, newSkill: e.target.value }));
  };

  const addSkill = () => {
    if (editData.newSkill.trim() && !editData.skills.includes(editData.newSkill.trim())) {
      setEditData(prev => ({
        ...prev,
        skills: [...prev.skills, prev.newSkill.trim()],
        newSkill: ''
      }));
    }
  };

  const removeSkill = (skillToRemove) => {
    setEditData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  // Save edited field
  const saveField = async (field) => {
    if (field === 'name' || field === 'username' || field === 'bio') {
      if (!editData[field].trim()) return;
    }
    
    try {
      const updateData = {};
      updateData[field] = editData[field];
      
      await onUpdateProfile(updateData);
      toggleEditMode(field, false);
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
    }
  };

  // Validate image before upload
  const validateImage = (file, maxSize) => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'File type not supported. Please use JPEG, PNG, or WebP format.';
    }
    
    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      return `File size too large. Maximum size is ${maxSizeMB}MB.`;
    }
    
    return '';
  };

  // Handle profile image selection
  const handleProfileImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImageError('');
    
    // Validate image (max 2MB)
    const validationError = validateImage(file, 2 * 1024 * 1024);
    if (validationError) {
      setImageError(validationError);
      return;
    }
    
    // Create a URL for the selected image
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowProfileCropModal(true);
  };
  
  // Handle cover image selection
  const handleCoverImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImageError('');
    
    // Validate image (max 5MB)
    const validationError = validateImage(file, 5 * 1024 * 1024);
    if (validationError) {
      setImageError(validationError);
      return;
    }
    
    // Create a URL for the selected image
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);
    setShowCoverCropModal(true);
  };
  
  // Handle saving the cropped profile image
  const handleSaveProfileImage = async (croppedBlob) => {
    try {
      // Create a file from the blob
      const file = new File([croppedBlob], 'profile-image.jpeg', { type: 'image/jpeg' });
      
      // Upload the cropped image
      await onUploadProfileImage(file);
    } catch (err) {
      console.error('Failed to upload profile picture:', err);
      setImageError('Failed to upload profile picture. Please try again.');
    } finally {
      // Clean up the object URL
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
        setSelectedImage(null);
      }
    }
  };
  
  // Handle saving the cropped cover image
  const handleSaveCoverImage = async (croppedBlob) => {
    try {
      // Create a file from the blob
      const file = new File([croppedBlob], 'cover-image.jpeg', { type: 'image/jpeg' });
      
      // Upload the cropped image
      await onUploadCoverImage(file);
    } catch (err) {
      console.error('Failed to upload cover picture:', err);
      setImageError('Failed to upload cover picture. Please try again.');
    } finally {
      // Clean up the object URL
      if (selectedImage) {
        URL.revokeObjectURL(selectedImage);
        setSelectedImage(null);
      }
    }
  };
  
  // Close crop modal
  const handleCloseCropModal = () => {
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
      setSelectedImage(null);
    }
    setShowProfileCropModal(false);
    setShowCoverCropModal(false);
  };
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Cover image */}
      <div className="h-48 bg-gray-200 relative">
        {profileUser.coverPicture ? (
          <img
            src={profileUser.coverPicture}
            alt={`${profileUser.name}'s cover`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-blue-400 to-indigo-500"></div>
        )}
        
        {/* Cover image upload progress */}
        {loading.coverPicture && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white">
            <div className="text-center">
              <div className="mb-2">Uploading...</div>
              <div className="w-48 bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${uploadProgress.coverPicture}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}
        
        {/* Edit cover image button (only for profile owner) */}
        {isProfileOwner && (
          <div className="absolute top-4 right-4">
            <input
              type="file"
              ref={coverImageInputRef}
              onChange={handleCoverImageChange}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
            />
            <Button 
              variant="light" 
              size="sm" 
              className="bg-white bg-opacity-80 hover:bg-opacity-100"
              onClick={() => coverImageInputRef.current.click()}
              isLoading={loading.coverPicture}
              disabled={loading.coverPicture || loading.profilePicture}
            >
              <svg 
                className="h-4 w-4 mr-1" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {loading.coverPicture ? 'Uploading...' : 'Edit Cover'}
            </Button>
          </div>
        )}
        
        {/* Profile image */}
        <div className="absolute bottom-0 left-8 transform translate-y-1/2 group">
          <Avatar
            src={profileUser.profilePicture}
            alt={profileUser.name}
            size="2xl"
            isOnline={profileUser.isOnline}
          />
          
          {/* Profile image upload progress */}
          {loading.profilePicture && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white">
              <div className="text-center">
                <div className="text-xs mb-1">Uploading...</div>
                <div className="w-12 bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-blue-600 h-1.5 rounded-full" 
                    style={{ width: `${uploadProgress.profilePicture}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
          
          {/* Edit profile picture overlay (only for profile owner) */}
          {isProfileOwner && (
            <>
              <input
                type="file"
                ref={profileImageInputRef}
                onChange={handleProfileImageChange}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
              />
              <div 
                className={`absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center ${
                  loading.profilePicture || loading.coverPicture 
                    ? 'opacity-100 cursor-not-allowed' 
                    : 'opacity-0 group-hover:opacity-100 cursor-pointer'
                } transition-opacity`}
                title={loading.profilePicture || loading.coverPicture 
                  ? 'Upload in progress...' 
                  : 'Change profile picture'}
                onClick={() => {
                  if (!loading.profilePicture && !loading.coverPicture) {
                    profileImageInputRef.current.click();
                  }
                }}
              >
                <svg 
                  className="h-8 w-8 text-white" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="absolute bottom-4 right-4 flex space-x-2">
          {isProfileOwner ? (
            <Link to="/settings/profile">
            </Link>
          ) : (
            <>
              {isAuth && (
                <>
                  <FollowButton
                    userId={profileUser._id}
                    isFollowing={isFollowing}
                    onFollowChange={onFollowChange}
                  />
                  <Link to={`/chat/${profileUser._id}`}>
                    <Button variant="light" size="sm">
                      Message
                    </Button>
                  </Link>
                </>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Error messages */}
      {(error.profilePicture || error.coverPicture || error.profile || imageError) && (
        <div className="px-8 pt-16">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {imageError || error.profilePicture || error.coverPicture || error.profile}
          </div>
        </div>
      )}
      
      {/* Profile info */}
      <div className="pt-16 px-8 pb-8">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="flex items-center">
              {editMode.name ? (
                <div className="flex-1 mr-2">
                  <input
                    type="text"
                    name="name"
                    value={editData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1 border border-gray-300 rounded-md text-xl font-bold"
                    placeholder="Your name"
                    autoFocus
                  />
                  <div className="flex mt-2 space-x-2">
                    <Button 
                      size="xs" 
                      variant="primary" 
                      onClick={() => saveField('name')}
                      disabled={!editData.name.trim() || loading.profile}
                      isLoading={loading.profile}
                    >
                      Save
                    </Button>
                    <Button 
                      size="xs" 
                      variant="light"
                      onClick={() => toggleEditMode('name', false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-gray-900">{profileUser.name}</h1>
                  {isProfileOwner && (
                    <button 
                      onClick={() => toggleEditMode('name', true)} 
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      <svg 
                        className="h-5 w-5" 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center">
              {editMode.username ? (
                <div className="flex-1 mr-2">
                  <input
                    type="text"
                    name="username"
                    value={editData.username}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1 border border-gray-300 rounded-md"
                    placeholder="username"
                    autoFocus
                  />
                  <div className="flex mt-2 space-x-2">
                    <Button 
                      size="xs" 
                      variant="primary" 
                      onClick={() => saveField('username')}
                      disabled={!editData.username.trim() || loading.profile}
                      isLoading={loading.profile}
                    >
                      Save
                    </Button>
                    <Button 
                      size="xs" 
                      variant="light"
                      onClick={() => toggleEditMode('username', false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-600">@{profileUser.username}</p>
                  {isProfileOwner && (
                    <button 
                      onClick={() => toggleEditMode('username', true)} 
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      <svg 
                        className="h-4 w-4" 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="mb-4">
          {editMode.bio ? (
            <div>
              <textarea
                name="bio"
                value={editData.bio}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Write something about yourself..."
                rows={3}
                autoFocus
              />
              <div className="flex mt-2 space-x-2">
                <Button 
                  size="sm" 
                  variant="primary" 
                  onClick={() => saveField('bio')}
                  disabled={loading.profile}
                  isLoading={loading.profile}
                >
                  Save
                </Button>
                <Button 
                  size="sm" 
                  variant="light"
                  onClick={() => toggleEditMode('bio', false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              {profileUser.bio ? (
                <div className="flex items-start">
                  <p className="text-gray-800 whitespace-pre-wrap flex-1">{profileUser.bio}</p>
                  {isProfileOwner && (
                    <button 
                      onClick={() => toggleEditMode('bio', true)} 
                      className="ml-2 text-gray-400 hover:text-gray-600 mt-1"
                    >
                      <svg 
                        className="h-4 w-4" 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>
              ) : isProfileOwner ? (
                <button 
                  onClick={() => toggleEditMode('bio', true)} 
                  className="text-blue-500 hover:text-blue-700 inline-flex items-center"
                >
                  <svg 
                    className="h-4 w-4 mr-1" 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add a bio
                </button>
              ) : null}
            </>
          )}
        </div>
        
        <div className="flex flex-wrap items-center text-sm text-gray-500 mb-4 gap-4">
          {profileUser.location && (
            <div className="flex items-center">
              <svg
                className="h-4 w-4 mr-1"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>{profileUser.location}</span>
            </div>
          )}
          
          {profileUser.website && (
            <div className="flex items-center">
              <svg
                className="h-4 w-4 mr-1"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              <a
                href={profileUser.website.startsWith('http') ? profileUser.website : `https://${profileUser.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {profileUser.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
          
          <div className="flex items-center">
            <svg
              className="h-4 w-4 mr-1"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Joined {joinedDate}</span>
          </div>
          
          {profileUser.lastActive && (
            <div className="flex items-center">
              <svg
                className="h-4 w-4 mr-1"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                Last active{' '}
                {new Date(profileUser.lastActive).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}
        </div>
        
        {/* Skills */}
        {editMode.skills && isProfileOwner ? (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-gray-700 font-medium">Skills</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={editData.newSkill}
                  onChange={handleSkillInputChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Add a skill..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addSkill();
                    }
                  }}
                />
                <Button size="sm" onClick={addSkill}>
                  Add
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 mt-2">
                {editData.skills.map((skill, index) => (
                  <div
                    key={index}
                    className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm flex items-center"
                  >
                    {skill}
                    <button
                      onClick={() => removeSkill(skill)}
                      className="ml-1 text-gray-500 hover:text-red-500"
                    >
                      <svg
                        className="h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-2">
                <Button 
                  size="sm" 
                  variant="light" 
                  onClick={() => toggleEditMode('skills', false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => saveField('skills')}
                  isLoading={loading.profile}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        ) : profileUser.skills && profileUser.skills.length > 0 ? (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-gray-700 font-medium">Skills</h2>
              {isProfileOwner && (
                <button
                  onClick={() => toggleEditMode('skills', true)}
                  className="text-blue-500 hover:text-blue-600 flex items-center"
                >
                  <svg
                    className="h-4 w-4 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  Edit
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {profileUser.skills.map((skill, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        ) : isProfileOwner ? (
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-gray-700 font-medium">Skills</h2>
            </div>
            <button 
              onClick={() => toggleEditMode('skills', true)} 
              className="text-blue-500 hover:text-blue-700 inline-flex items-center"
            >
              <svg 
                className="h-4 w-4 mr-1" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add skills
            </button>
          </div>
        ) : null}
        
        {/* Follower stats */}
        <div className="flex space-x-6">
          <Link
            to={`/profile/${profileUser.username}/following`}
            className="text-gray-900 hover:underline"
          >
            <span className="font-bold">{profileUser.followingCount || 0}</span>{' '}
            <span className="text-gray-600">Following</span>
          </Link>
          <Link
            to={`/profile/${profileUser.username}/followers`}
            className="text-gray-900 hover:underline"
          >
            <span className="font-bold">{profileUser.followersCount || 0}</span>{' '}
            <span className="text-gray-600">Followers</span>
          </Link>
        </div>
      </div>
      
      {/* Image Crop Modals */}
      {/* Profile Image Crop Modal */}
      {showProfileCropModal && selectedImage && (
        <ImageCropModal
          isOpen={showProfileCropModal}
          onClose={handleCloseCropModal}
          image={selectedImage}
          onSave={handleSaveProfileImage}
          title="Edit Profile Picture"
          aspectRatio={{ width: 1, height: 1 }}
          recommendedSize={{ width: 400, height: 400 }}
        />
      )}
      
      {/* Cover Image Crop Modal */}
      {showCoverCropModal && selectedImage && (
        <ImageCropModal
          isOpen={showCoverCropModal}
          onClose={handleCloseCropModal}
          image={selectedImage}
          onSave={handleSaveCoverImage}
          title="Edit Cover Picture"
          aspectRatio={{ width: 3, height: 1 }}
          recommendedSize={{ width: 1200, height: 400 }}
        />
      )}
    </div>
  );
};

ProfileHeader.propTypes = {
  profileUser: PropTypes.object.isRequired,
  isFollowing: PropTypes.bool,
  onFollowChange: PropTypes.func,
  isProfileOwner: PropTypes.bool,
  onUpdateProfile: PropTypes.func,
  onUploadProfileImage: PropTypes.func,
  onUploadCoverImage: PropTypes.func,
  loading: PropTypes.object,
  error: PropTypes.object,
  uploadProgress: PropTypes.object
};

export default ProfileHeader;